import isError from 'lodash/isError'
import { Modal } from '@klear/ui/components/modal'
import { Label } from '@klear/ui/components/label'
import { Textarea } from '@klear/ui/components/textarea'
import { type ActionConfig } from '@klear/ui/hooks/use-event-action-config'
import { PaymentPreview } from '@klear/ui/components/payment-preview'
import type { OrderEventActionFormValues } from '@klear/order-event/event-form'
import type { OrderEventActionEnum } from '@klear/order-event/constants'
import { orderEventActionLabel } from '@klear/order-event/presentation'
import { PERMISSION_ACTION_COMPLETE, PERMISSION_ENTITY_PAYMENT } from '@klear/constants'
import { toast } from '@klear/ui/hooks/use-toast'
import { usePaymentPreviewForComplete } from '@klear/order-event-ui/use-payment-preview-accounts'
import type { OrderEventRef } from '@klear/ui/hooks/order-event-ref'

interface EventActionsConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (action?: OrderEventActionEnum) => void
  eventRef: OrderEventRef
  parentEventId?: number
  selectedAction: OrderEventActionEnum | null
  actionConfig: ActionConfig | null
  actionValues: OrderEventActionFormValues
  onNotesChange: (notes: string) => void
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
}

export const EventActionsConfirmModal = ({
  isOpen,
  onClose,
  onSuccess,
  eventRef,
  parentEventId,
  selectedAction,
  actionConfig,
  actionValues,
  onNotesChange,
  isLoading,
  setIsLoading
}: EventActionsConfirmModalProps) => {
  const isPaymentComplete = selectedAction === PERMISSION_ACTION_COMPLETE && eventRef.type === PERMISSION_ENTITY_PAYMENT

  const {
    payment,
    sourceAccount,
    destinationAccount,
    isLoading: isAccountsLoading
  } = usePaymentPreviewForComplete(eventRef, parentEventId, isPaymentComplete)

  const modalConfig = {
    size: (isPaymentComplete ? 'lg' : 'sm') as 'lg' | 'sm',
    title:
      isPaymentComplete || selectedAction == null
        ? isPaymentComplete
          ? 'Send Payment Now?'
          : ''
        : `${orderEventActionLabel(eventRef.type, selectedAction)}?`,
    description: isPaymentComplete
      ? 'Review the payment details before sending. This action cannot be undone.'
      : (actionConfig?.description ?? 'This action cannot be undone.'),
    primaryText: isLoading ? 'Processing...' : isPaymentComplete ? 'Send Now' : 'Continue'
  }

  const handleSubmit = async () => {
    if (!selectedAction || !actionConfig) return
    setIsLoading(true)
    try {
      await actionConfig.action([eventRef], actionValues)
      onSuccess?.(selectedAction)
      onClose()
    } catch (error) {
      console.error('Error:', error)
      toast({
        title: 'Error',
        description: isError(error) ? error.message : 'An error occurred while performing the action',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      size={modalConfig.size}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      title={modalConfig.title}
      description={modalConfig.description}
      onSubmit={handleSubmit}
      primaryText={modalConfig.primaryText}
      primaryDisabled={isLoading}
      secondaryText="Cancel"
      secondaryDisabled={isLoading}
      handleSecondaryAction={onClose}
      handlePrimaryAction={handleSubmit}
    >
      <div className="grid grid-cols-1 gap-4">
        {isPaymentComplete ? (
          isAccountsLoading ? (
            <p className="text-sm text-muted-foreground">Loading payment details...</p>
          ) : sourceAccount && destinationAccount && payment ? (
            <PaymentPreview payment={payment} sourceAccount={sourceAccount} destinationAccount={destinationAccount} />
          ) : (
            <p className="text-sm text-muted-foreground">
              Account details could not be loaded for preview. You can still send the payment if the accounts are
              configured correctly.
            </p>
          )
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="action-notes">Notes (optional)</Label>
          <Textarea
            id="action-notes"
            value={actionValues.notes ?? ''}
            disabled={isLoading}
            onChange={(e) => onNotesChange(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  )
}
